import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore as createReduxStore,
} from "redux";

import { Provider, useDispatch } from "react-redux";
import thunk, { ThunkAction, ThunkMiddleware } from "redux-thunk";

import * as favouritesStore from "./favourites";
import * as opportunityStore from "./opportunity";
import * as opportunityResultStore from "./opportunityResult";
import * as organisationStore from "./organisation";
import * as organisationResultStore from "./organisationResult";

import {
  OpportunitySearchQueryState,
  OrganisationSearchQueryState,
} from "./context";

import * as React from "react";

import {
  CustomError,
  Opportunity,
  OpportunityResultStateEnums,
  OpportunitySearchResults,
  PageEnvelope,
  SearchResultStatus,
} from "@skv/core";

export * from "./context";

export {
  favouritesStore,
  opportunityResultStore,
  opportunityStore,
  organisationResultStore,
  organisationStore,
};

export const reducer = combineReducers({
  opportunityResult: opportunityResultStore.reducer,
  opportunity: opportunityStore.reducer,
  organisation: organisationStore.reducer,
  favourites: favouritesStore.reducer,
  organisationResult: organisationResultStore.reducer,
});

export type RootState = ReturnType<typeof reducer>;

export type RootActions =
  | opportunityResultStore.ActionTypes
  | opportunityStore.ActionTypes
  | organisationStore.ActionTypes
  | favouritesStore.ActionTypes
  | organisationResultStore.ActionTypes;

declare const window: {
  __REDUX_DEVTOOLS_EXTENSION__: any;
};

const ReduxDevToolsMiddleware = ((): any => {
  if (process.env.NODE_ENV !== "production") {
    if (typeof window !== "undefined" && window.__REDUX_DEVTOOLS_EXTENSION__) {
      return window.__REDUX_DEVTOOLS_EXTENSION__({ trace: true });
    }
  }

  return (f): any => f;
})();

export const createStore = (initialState?: Partial<RootState>) => {
  return createReduxStore(
    reducer,
    initialState,
    compose(
      applyMiddleware(thunk as ThunkMiddleware<RootState, RootActions>),
      ReduxDevToolsMiddleware
    )
  );
};

type ThunkDispatch = ReturnType<typeof createStore>["dispatch"];

export const useThunkDispatch = () => {
  return useDispatch<ThunkDispatch>();
};

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  RootActions
>;

type InitialServerPageEnvelope = {
  opportunityResult?: OpportunitySearchResults;
  opportunity?: Opportunity;
  organisation?: organisationStore.Organisation;
  organisationResult?: organisationResultStore.OrganisationSearchResults;
  favourites?: any;

  searchCriteria?: OpportunitySearchQueryState | OrganisationSearchQueryState;
};

type InitialServerMeta = {
  id?: number;
} | null;

const handleError = (
  pageEnvelope: PageEnvelope<InitialServerPageEnvelope, InitialServerMeta>,
  initialState: RootState
): RootState => {
  const { meta } = pageEnvelope;

  // error:namespace:errordesc

  const err = pageEnvelope.errorCode.split(":");

  if (err[0] !== "error") {
    return initialState;
  }

  const key = err[1];
  const key2 = err[2];

  // if (!initialState[key]) {
  //   return initialState;
  // }

  switch (key) {
    case "application":
      if (key2 === "alreadyexists") {
        initialState.opportunity.error = new CustomError(
          pageEnvelope.errorMessage,
          pageEnvelope.errorCode
        );
        initialState.opportunity.data = {
          [pageEnvelope.data.opportunity.id]: initialState.opportunity.error,
        };
      }

      break;
    case "organisation":
    case "opportunity": {
      initialState[key].error = new CustomError(
        pageEnvelope.errorMessage,
        pageEnvelope.errorCode
      );
      initialState[key].data = {
        [meta?.id]: initialState[key].error,
      };
      break;
    }
  }

  return initialState;
};

export const createInitialState = <
  T extends InitialServerPageEnvelope,
  M extends InitialServerMeta
>(
  pageEnvelope: PageEnvelope<T, M>
): RootState => {
  const { opportunities, organisations } =
    favouritesStore.getFavouritesFromCookie();

  let initialState: RootState = {
    opportunityResult: {
      loading: false,
      error: null,
      data: {},
      state: OpportunityResultStateEnums.IDLE,
    },
    opportunity: {
      loading: false,
      error: null,
      data: {},
    },
    organisation: {
      loading: false,
      error: null,
      data: {},
    },
    organisationResult: {
      loading: false,
      error: null,
      data: {},
      status: SearchResultStatus.IDLE,
    },
    favourites: {
      loading: false,
      error: null,
      opportunities,
      organisations,
    },
  };

  if (pageEnvelope) {
    if (pageEnvelope.errorCode) {
      initialState = handleError(pageEnvelope, initialState);
    }

    if (pageEnvelope.data) {
      if ("opportunityResult" in pageEnvelope.data) {
        initialState.opportunityResult = {
          ...initialState.opportunityResult,
          data: {
            [opportunityResultStore.util.opportunitySearchQueryToString(
              pageEnvelope.data.searchCriteria as OpportunitySearchQueryState
            )]: pageEnvelope.data.opportunityResult,
          },
        };
      }

      if ("opportunity" in pageEnvelope.data) {
        initialState.opportunity = {
          ...initialState.opportunity,
          data: {
            [pageEnvelope.data.opportunity.id]: pageEnvelope.data.opportunity,
          },
        };
      }

      if ("organisation" in pageEnvelope.data) {
        initialState.organisation = {
          ...initialState.organisation,
          data: {
            [pageEnvelope.data.organisation.id]: pageEnvelope.data.organisation,
          },
        };
      }

      if ("organisationResult" in pageEnvelope.data) {
        initialState.organisationResult = {
          ...initialState.organisationResult,
          data: {
            [organisationResultStore.util.organisationSearchQueryToString(
              pageEnvelope.data.searchCriteria
            )]: pageEnvelope.data.organisationResult,
          },
        };
      }
    }
  }

  return initialState;
};

export const ReduxProvider: React.FC<{
  initialPageEnvelope: PageEnvelope<unknown, unknown>;
}> = ({ initialPageEnvelope, children }) => {
  const initialState = React.useMemo(() => {
    return createInitialState(initialPageEnvelope);
  }, [initialPageEnvelope]);

  const store = createStore(initialState);

  return <Provider store={store}>{children}</Provider>;
};

export * from "./SearchForm.state";
